import * as styles from "./PdfViewerToolbar.css";

import type { JSX } from "react";

import { useRotate } from "@embedpdf/plugin-rotate/react";
import { useScroll } from "@embedpdf/plugin-scroll/react";
import { useZoom } from "@embedpdf/plugin-zoom/react";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { Separator } from "@frontend/components/atoms/Separator";
import { Toggle } from "@frontend/components/atoms/Toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import {
  Toolbar,
  ToolbarSection,
} from "@frontend/components/molecules/Toolbar";
import { useLingui } from "@lingui/react/macro";

import { usePdfViewerContext } from "./PdfViewer.context";
import type { PdfViewerToolbarProps } from "./PdfViewer.types";
import { PdfViewerSearch } from "./PdfViewerSearch";
import { PDF_VIEWER_ZOOM_PRESETS } from "./constants";
import { toZoomLevel } from "./plugins";

function percent(zoom: number): string {
  return `${Math.round(zoom * 100)}%`;
}

export function PdfViewerToolbar({
  pageCount,
}: PdfViewerToolbarProps): JSX.Element {
  const { t } = useLingui();

  const {
    documentId,
    src,
    name,
    tools,
    thumbnailsOpen,
    setThumbnailsOpen,
    searchOpen,
    setSearchOpen,
    onClose,
  } = usePdfViewerContext();

  const { provides: scroll, state: scrollState } = useScroll(documentId);
  const { provides: zoom, state: zoomState } = useZoom(documentId);
  const { provides: rotate } = useRotate(documentId);

  const totalPages = pageCount || scrollState.totalPages;

  const has = (tool: (typeof tools)[number]) => tools.includes(tool);

  const commitPage = (input: HTMLInputElement) => {
    const pageNumber = Number(input.value);

    if (
      scroll === null ||
      Number.isNaN(pageNumber) ||
      pageNumber < 1 ||
      pageNumber > totalPages
    ) {
      input.value = String(scrollState.currentPage);

      return;
    }

    scroll.scrollToPage({ pageNumber });
  };

  const zoomValue =
    typeof zoomState.zoomLevel === "number"
      ? String(zoomState.zoomLevel)
      : zoomState.zoomLevel;

  const zoomPresets: number[] = [...PDF_VIEWER_ZOOM_PRESETS];

  const zoomItems = [
    { value: "fit-width", label: t`Fit width` },
    { value: "fit-page", label: t`Fit page` },
    { value: "automatic", label: t`Automatic` },
    ...zoomPresets.map((preset) => ({
      value: String(preset),
      label: percent(preset),
    })),
  ];

  if (!zoomItems.some((item) => item.value === zoomValue)) {
    zoomItems.push({
      value: zoomValue,
      label: percent(zoomState.currentZoomLevel),
    });
  }

  return (
    <>
      <Toolbar className={styles.toolbar} align="between" wrap={false}>
        <ToolbarSection className={styles.group}>
          {has("thumbnails") ? (
            <span className={styles.paneToggle}>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Toggle
                      size="sm"
                      pressed={thumbnailsOpen}
                      onPressedChange={setThumbnailsOpen}
                      aria-label={t`Page thumbnails`}
                    >
                      <Icon name="PanelLeft" size="sm" />
                    </Toggle>
                  }
                />
                <TooltipContent>{t`Page thumbnails`}</TooltipContent>
              </Tooltip>
            </span>
          ) : null}

          {has("pages") ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                aria-label={t`Previous page`}
                disabled={scrollState.currentPage <= 1}
                onClick={() => scroll?.scrollToPreviousPage()}
              >
                <Icon name="ChevronUp" size="sm" />
              </Button>

              <Button
                size="sm"
                variant="ghost"
                aria-label={t`Next page`}
                disabled={scrollState.currentPage >= totalPages}
                onClick={() => scroll?.scrollToNextPage()}
              >
                <Icon name="ChevronDown" size="sm" />
              </Button>

              <Input
                // Remounting on every move keeps the field showing the page
                // the document is on, without mirroring it into state.
                key={scrollState.currentPage}
                size="sm"
                className={styles.pageInput}
                inputMode="numeric"
                aria-label={t`Page`}
                defaultValue={String(scrollState.currentPage)}
                onBlur={(event) => commitPage(event.currentTarget)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    commitPage(event.currentTarget);
                  }
                }}
              />

              <span className={styles.pageCount}>{t`of ${totalPages}`}</span>
            </>
          ) : null}
        </ToolbarSection>

        <ToolbarSection className={styles.group}>
          {has("zoom") ? (
            <>
              <Separator orientation="vertical" className={styles.divider} />

              <div className={styles.zoom}>
                <span className={styles.wideOnly}>
                  <Select
                    size="sm"
                    items={zoomItems}
                    value={zoomValue}
                    onValueChange={(value) => {
                      if (typeof value !== "string") {
                        return;
                      }

                      const preset = Number(value);

                      zoom?.requestZoom(
                        toZoomLevel(
                          Number.isNaN(preset)
                            ? (value as "automatic" | "fit-page" | "fit-width")
                            : preset,
                        ),
                      );
                    }}
                  >
                    <SelectTrigger
                      variant="ghost"
                      className={styles.zoomTrigger}
                    >
                      <SelectValue>
                        {() => percent(zoomState.currentZoomLevel)}
                      </SelectValue>
                    </SelectTrigger>

                    <SelectContent
                      alignItemWithTrigger={false}
                      popupWidth="content"
                    >
                      {zoomItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </span>

                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t`Zoom out`}
                  onClick={() => zoom?.zoomOut()}
                >
                  <Icon name="CircleMinus" size="sm" />
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t`Zoom in`}
                  onClick={() => zoom?.zoomIn()}
                >
                  <Icon name="CirclePlus" size="sm" />
                </Button>
              </div>

              <Separator orientation="vertical" className={styles.divider} />
            </>
          ) : null}
        </ToolbarSection>

        <ToolbarSection className={styles.group}>
          {has("rotate") ? (
            <span className={styles.roomyOnly}>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t`Rotate`}
                      onClick={() => rotate?.rotateForward()}
                    >
                      <Icon name="RotateCw" size="sm" />
                    </Button>
                  }
                />
                <TooltipContent>{t`Rotate`}</TooltipContent>
              </Tooltip>
            </span>
          ) : null}

          {has("search") ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Toggle
                    size="sm"
                    pressed={searchOpen}
                    onPressedChange={setSearchOpen}
                    aria-label={t`Search`}
                  >
                    <Icon name="Search" size="sm" />
                  </Toggle>
                }
              />
              <TooltipContent>{t`Search`}</TooltipContent>
            </Tooltip>
          ) : null}

          {has("download") ? (
            <span className={styles.narrowHidden}>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t`Download`}
                      render={<a href={src} download={name} />}
                    >
                      <Icon name="Download" size="sm" />
                    </Button>
                  }
                />
                <TooltipContent>{t`Download`}</TooltipContent>
              </Tooltip>
            </span>
          ) : null}

          {onClose === undefined ? null : (
            <>
              <Separator orientation="vertical" className={styles.divider} />

              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t`Close`}
                      onClick={onClose}
                    >
                      <Icon name="X" size="sm" />
                    </Button>
                  }
                />
                <TooltipContent>{t`Close`}</TooltipContent>
              </Tooltip>
            </>
          )}
        </ToolbarSection>
      </Toolbar>

      {searchOpen ? <PdfViewerSearch /> : null}
    </>
  );
}
